import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import DcPage from '@/components/landing/dcv2/DcPage';
import { ForumProvider } from '@/features/forum/ForumContext';
import { useForum } from '@/features/forum/useForum';
import { ThreadList } from '@/features/forum/components/ThreadList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ForumCardSkeleton } from '@/features/forum/components/ForumCardSkeleton';
import { ArrowLeft, FolderOpen, Search, Plus } from 'lucide-react';
import { displaySpaceName, initialsOf } from "@/features/forum/forum-display";

function ForumSubforumContent() {
  const { slug } = useParams<{ slug: string }>();
  const { getSubforumBySlug, fetchPosts, fetchSubforums, state } = useForum();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    if (state.subforums.length === 0 && !state.loading.subforums) {
      fetchSubforums();
    }
  }, [state.subforums.length, state.loading.subforums, fetchSubforums]);

  const subforum = slug ? getSubforumBySlug(slug) : null;

  useEffect(() => {
    if (subforum) {
      fetchPosts(subforum.id);
    }
  }, [subforum, fetchPosts]);

  if (state.loading.subforums) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--landing-bg)' }}>
        <header className="pt-24 pb-10 px-6 border-b" style={{ borderColor: 'var(--landing-glass-border)' }}>
          <div className="container mx-auto max-w-6xl">
            <div className="h-4 w-24 rounded animate-pulse mb-4" style={{ background: 'var(--landing-surface)' }} />
            <div className="h-8 w-64 rounded-xl animate-pulse" style={{ background: 'var(--landing-surface)' }} />
            <div className="h-4 w-full max-w-96 mt-3 rounded animate-pulse" style={{ background: 'var(--landing-surface)' }} />
          </div>
        </header>
        <main className="pb-24 px-6 pt-10" style={{ background: 'var(--landing-bg)' }}>
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <ForumCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!subforum) {
    return (
      <section
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
        style={{ background: 'var(--landing-bg)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(var(--landing-grid-line) 1px, transparent 1px),
              linear-gradient(90deg, var(--landing-grid-line) 1px, transparent 1px)
            `,
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black, transparent)',
          }}
        />
        <div className="text-center max-w-md mx-auto px-6 relative z-10 space-y-4">
          <div
            className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: 'var(--landing-surface)', border: '1px solid var(--landing-glass-border)' }}
          >
            <FolderOpen className="h-8 w-8 text-[var(--acc2)]/60" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--landing-text)' }}>
            Subforum not found
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--landing-text-muted)' }}>
            This subforum doesn't exist or may have been removed.
          </p>
          <Link
            to="/forum"
            className="btn-electric px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2"
          >
            Back to Forum
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* ═══ HERO ═══ */}
      <section
        className="relative overflow-hidden pt-24 md:pt-28 pb-6"
        style={{ background: 'var(--landing-bg)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(var(--landing-grid-line) 1px, transparent 1px),
              linear-gradient(90deg, var(--landing-grid-line) 1px, transparent 1px)
            `,
            backgroundSize: '72px 72px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent)',
          }}
        />

        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-6xl"
          >
            <Link
              to="/forum"
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-6 transition-colors hover:text-[var(--acc2)]"
              style={{ color: 'rgba(var(--ink-rgb),.62)' }}
            >
              <ArrowLeft size={14} />
              Forum
            </Link>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="flex items-start gap-4 max-w-2xl">
                {/* Initials, not a generic speech bubble. Every space drew
                    the same icon, so it identified the category rather than the
                    space; the rows below already carry this same mark. */}
                <div
                  className="h-12 w-12 rounded-xl grid place-items-center flex-shrink-0 mt-1 text-[14px] font-bold"
                  style={{
                    background: 'rgba(var(--acc2-rgb),0.12)',
                    color: 'var(--acc2)',
                  }}
                >
                  {initialsOf(subforum.name)}
                </div>
                <div>
                  <h1
                    className="text-[22px] sm:text-[26px] font-bold tracking-[-0.02em] leading-[1.1] mb-2"
                    style={{ color: 'var(--landing-text)' }}
                  >
                    <span
                      style={{ color: "var(--acc2)" }}
                    >
                      {displaySpaceName(subforum.name)}
                    </span>
                  </h1>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--landing-text-muted)' }}>
                    {subforum.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 w-full lg:w-auto lg:min-w-[420px]">
                <div
                  className="flex min-w-0 items-center gap-2 p-1.5 rounded-2xl flex-1"
                  style={{
                    background: 'var(--landing-glass-bg)',
                    border: '1px solid var(--landing-glass-border)',
                    boxShadow: 'var(--landing-card-shadow)',
                  }}
                >
                  <div className="min-w-0 flex-1 relative">
                    <Search
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4"
                      style={{ color: 'rgba(var(--ink-rgb),.62)' }}
                    />
                    <Input
                      placeholder="Search discussions…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 border-0 bg-transparent focus-visible:ring-0 text-sm"
                      style={{ color: 'var(--landing-text)' }}
                    />
                  </div>
                </div>
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="btn-electric h-11 px-5 rounded-xl text-sm font-semibold"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  New Post
                </Button>
              </div>
            </div>
          </motion.div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-12"
          style={{ background: 'linear-gradient(to top, var(--landing-bg), transparent)' }}
        />
      </section>

      {/* ═══ THREAD LIST ═══ */}
      {/* Top padding is small on purpose: the header section above already
          carries its own bottom padding, and the two together left roughly
          100px of nothing between the space name and the list it labels. */}
      <section style={{ background: 'var(--landing-bg)' }}>
        <div className="container pt-1 pb-16 md:pb-20">
          <div className="mx-auto max-w-6xl">
            <ThreadList
              subforumId={subforum.id}
              searchQuery={searchQuery}
              showCreateForm={showCreateForm}
              onCloseCreateForm={() => setShowCreateForm(false)}
            />
          </div>
        </div>
      </section>
    </>
  );
}

export default function ForumSubforum() {
  return (
    <ForumProvider>
      <DcPage>
        <ForumSubforumContent />
      </DcPage>
    </ForumProvider>
  );
}
